/**
 * Login com Google.
 *
 * - Android nativo (Capacitor): usa @codetrix-studio/capacitor-google-auth
 *   (Google Sign-In SDK nativo — não abre browser externo)
 *
 * - Web em produção: abre popup em saude.<baseDomain>/auth/google-proxy
 *   → GSI roda lá (único domínio registrado no Google Console)
 *   → credential chega via postMessage
 *
 * - Localhost / dev: carrega GSI diretamente
 */
import { Capacitor } from '@capacitor/core';

export function useGoogleAuth() {
  const config = useRuntimeConfig();
  const clientId = config.public.googleClientId as string;
  const baseDomain = config.public.baseDomain as string;
  const enabled = computed(() => !!clientId);

  const isLocalDev = computed(() =>
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  );

  // ── Android nativo ──────────────────────────────────────────────────────────

  async function signInNative(): Promise<string> {
    const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
    await GoogleAuth.initialize({
      clientId,
      scopes: ['profile', 'email'],
      grantOfflineAccess: false,
    });
    const user = await GoogleAuth.signIn();
    const idToken = user.authentication?.idToken;
    if (!idToken) throw new Error('Google Sign-In não retornou idToken.');
    return idToken;
  }

  // ── Popup proxy (produção web) ──────────────────────────────────────────────

  function signInViaProxy(): Promise<string> {
    return new Promise((resolve, reject) => {
      const origin = encodeURIComponent(window.location.origin);
      const proxyUrl = `https://${baseDomain}/auth/google-proxy?origin=${origin}`;

      const popup = window.open(proxyUrl, 'google-auth',
        'width=480,height=560,left=200,top=100,scrollbars=no,resizable=no');

      if (!popup) {
        reject(new Error('Popup bloqueado pelo navegador. Permita popups para este site e tente novamente.'));
        return;
      }

      const rootOrigin = `https://${baseDomain}`;

      function onMessage(event: MessageEvent) {
        if (event.origin !== rootOrigin) return;
        if (event.data?.type !== 'google-credential') return;
        cleanup();
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data.credential);
        }
      }

      const pollClosed = setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new Error('Login cancelado.'));
        }
      }, 600);

      function cleanup() {
        clearInterval(pollClosed);
        window.removeEventListener('message', onMessage);
      }

      window.addEventListener('message', onMessage);
    });
  }

  // ── GSI direto (localhost / dev) ────────────────────────────────────────────

  function loadGSI(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).google?.accounts?.id) return resolve();
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Falha ao carregar Google GSI'));
      document.head.appendChild(s);
    });
  }

  function signInDirect(): Promise<string> {
    return loadGSI().then(() => new Promise((resolve, reject) => {
      const google = (window as any).google;
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (r: { credential: string }) => resolve(r.credential),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      google.accounts.id.prompt((n: any) => {
        if (n.isNotDisplayed() || n.isSkippedMoment()) {
          reject(new Error('Login Google cancelado ou bloqueado pelo browser.'));
        }
      });
    }));
  }

  // ── API pública ─────────────────────────────────────────────────────────────

  async function signInWithGoogle(): Promise<string> {
    if (typeof window === 'undefined') throw new Error('SSR');
    if (Capacitor.isNativePlatform()) return signInNative();
    return isLocalDev.value ? signInDirect() : signInViaProxy();
  }

  return { enabled, signInWithGoogle };
}
